<template>
  <section class="financeiro-section financeiro-resultado">
    <div class="financeiro-filters">
      <v-select
        :options="periodOptions"
        :reduce="(o) => o.id"
        v-model="periodRange"
        placeholder="Período"
        :clearable="false"
      />
      <v-select
        :options="accountFilterOptions"
        :reduce="(o) => o.id"
        v-model="accountFilter"
        placeholder="Conta"
        :clearable="false"
      />
      <template v-if="periodRange === 'custom'">
        <input v-model="customStartDate" type="date" class="financeiro-date-field" aria-label="Data inicial" />
        <input v-model="customEndDate" type="date" class="financeiro-date-field" aria-label="Data final" />
      </template>
    </div>

    <div class="financeiro-kpis card-list">
      <article class="card card--with-icon financeiro-kpi">
        <div class="card-icon-wrap financeiro-kpi-in">
          <i class="material-icons">trending_up</i>
        </div>
        <div class="card-content">
          <span class="financeiro-kpi-value">{{ formatBrl(totalIncome) }}</span>
          <p class="card-meta card-secondary">Receitas</p>
        </div>
      </article>
      <article class="card card--with-icon financeiro-kpi">
        <div class="card-icon-wrap financeiro-kpi-out">
          <i class="material-icons">trending_down</i>
        </div>
        <div class="card-content">
          <span class="financeiro-kpi-value">{{ formatBrl(totalExpense) }}</span>
          <p class="card-meta card-secondary">Despesas</p>
        </div>
      </article>
      <article class="card card--with-icon financeiro-kpi">
        <div class="card-icon-wrap financeiro-kpi-net">
          <i class="material-icons">account_balance_wallet</i>
        </div>
        <div class="card-content">
          <span class="financeiro-kpi-value" :class="netResult >= 0 ? 'amount-in' : 'amount-out'">
            {{ formatBrl(netResult) }}
          </span>
          <p class="card-meta card-secondary">Resultado</p>
        </div>
      </article>
    </div>

    <div v-if="incomeRows.length > 0" class="financeiro-resultado-block card card--panel">
      <h2 class="financeiro-subheading">Receitas por categoria</h2>
      <div class="financeiro-chart-container">
        <apexchart width="100%" type="bar" :series="incomeChartSeries" :options="incomeChartOptions" />
      </div>
      <table class="financeiro-summary-table">
        <thead>
          <tr>
            <th>Categoria</th>
            <th class="num">Valor</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in incomeRows" :key="row.id">
            <td>{{ row.name }}</td>
            <td class="num amount-in">{{ formatBrl(row.amount) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th>Total</th>
            <th class="num amount-in">{{ formatBrl(totalIncome) }}</th>
          </tr>
        </tfoot>
      </table>
    </div>

    <div v-if="expenseRows.length > 0" class="financeiro-resultado-block card card--panel">
      <h2 class="financeiro-subheading">Despesas por categoria</h2>
      <div class="financeiro-chart-container">
        <apexchart width="100%" type="bar" :series="expenseChartSeries" :options="expenseChartOptions" />
      </div>
      <table class="financeiro-summary-table">
        <thead>
          <tr>
            <th>Categoria</th>
            <th class="num">Valor</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in expenseRows" :key="row.id">
            <td>{{ row.name }}</td>
            <td class="num amount-out">{{ formatBrl(row.amount) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th>Total</th>
            <th class="num amount-out">{{ formatBrl(totalExpense) }}</th>
          </tr>
        </tfoot>
      </table>
    </div>

    <p v-if="incomeRows.length === 0 && expenseRows.length === 0" class="empty-hint">
      Nenhuma receita ou despesa no período.
    </p>
  </section>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch } from 'vue';
import { useResultadoReport } from '@/composables/useResultadoReport';

const PERIOD_MS = {
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
};

export default {
  name: 'FinanceiroResultadoTab',
  components: { apexchart: VueApexCharts, vSelect },
  props: {
    accounts: { type: Array, default: () => [] },
    allTransactions: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    filterByDateRange: { type: Function, required: true },
    toDateKey: { type: Function, required: true },
  },
  setup(props) {
    const periodOptions = [
      { id: 'today', label: 'Hoje' },
      { id: 'month', label: 'Este mês' },
      { id: '30d', label: 'Últimos 30 dias' },
      { id: '90d', label: 'Últimos 90 dias' },
      { id: 'year', label: 'Este ano' },
      { id: 'custom', label: 'Personalizado' },
    ];

    const periodRange = ref('month');
    const accountFilter = ref('all');
    const customStartDate = ref('');
    const customEndDate = ref('');

    const accountFilterOptions = computed(() => [
      { id: 'all', label: 'Todas as contas' },
      ...props.accounts.map((a) => ({ id: a.id, label: a.name })),
    ]);

    function getDateRange(preset) {
      const now = new Date();
      const nowMs = now.getTime();
      if (preset === 'custom') {
        const start = customStartDate.value
          ? new Date(customStartDate.value + 'T00:00:00').getTime()
          : nowMs - PERIOD_MS['30d'];
        const end = customEndDate.value
          ? new Date(customEndDate.value + 'T23:59:59').getTime()
          : nowMs;
        return { min: start, max: end };
      }
      if (preset === 'year') {
        return { min: new Date(now.getFullYear(), 0, 1).getTime(), max: nowMs };
      }
      if (preset === 'month') {
        return { min: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), max: nowMs };
      }
      if (preset === 'today') {
        return { min: new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(), max: nowMs };
      }
      const ms = PERIOD_MS[preset];
      return { min: nowMs - (ms || PERIOD_MS['30d']), max: nowMs };
    }

    const filteredTransactions = computed(() => {
      const { min, max } = getDateRange(periodRange.value);
      let list = props.filterByDateRange(props.allTransactions, min, max);
      if (accountFilter.value && accountFilter.value !== 'all') {
        list = list.filter((tx) => tx.accountId === accountFilter.value);
      }
      return list;
    });

    const {
      incomeRows,
      expenseRows,
      totalIncome,
      totalExpense,
      netResult,
    } = useResultadoReport(filteredTransactions, computed(() => props.categories));

    watch(periodRange, (newVal, oldVal) => {
      if (newVal === 'custom' && oldVal && oldVal !== 'custom') {
        const range = getDateRange(oldVal);
        customStartDate.value = props.toDateKey(range.min);
        customEndDate.value = props.toDateKey(range.max);
      }
    });

    function formatBrl(n) {
      return Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    function buildCategoryChartOptions(rows, color) {
      return {
        chart: {
          toolbar: { show: false },
          fontFamily: 'var(--font-sans)',
          parentHeightOffset: 0,
        },
        xaxis: {
          categories: rows.map((r) => r.name),
          labels: {
            trim: true,
            hideOverlappingLabels: true,
            style: { fontSize: '11px' },
          },
        },
        colors: [color],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: { padding: { left: 8, right: 8 } },
        yaxis: {
          labels: {
            formatter: (v) => formatBrl(v),
            style: { fontSize: '11px' },
            maxWidth: 72,
          },
        },
        tooltip: { y: { formatter: (v) => formatBrl(v) } },
      };
    }

    const incomeChartSeries = computed(() => [{
      name: 'Receitas',
      data: incomeRows.value.map((r) => r.amount),
    }]);

    const expenseChartSeries = computed(() => [{
      name: 'Despesas',
      data: expenseRows.value.map((r) => r.amount),
    }]);

    const incomeChartOptions = computed(() =>
      buildCategoryChartOptions(incomeRows.value, '#5a8f5a')
    );

    const expenseChartOptions = computed(() =>
      buildCategoryChartOptions(expenseRows.value, '#b07d7d')
    );

    return {
      periodOptions,
      periodRange,
      accountFilter,
      customStartDate,
      customEndDate,
      accountFilterOptions,
      incomeRows,
      expenseRows,
      totalIncome,
      totalExpense,
      netResult,
      incomeChartSeries,
      expenseChartSeries,
      incomeChartOptions,
      expenseChartOptions,
      formatBrl,
    };
  },
};
</script>

<style scoped>
.financeiro-filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
  align-items: center;
}

.financeiro-filters :deep(.v-select) {
  flex: 1 1 160px;
  min-width: 160px;
}

.financeiro-date-field {
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: inherit;
}

.financeiro-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
}

.financeiro-kpi-value {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.financeiro-kpi-in { background: #e8f5e9; color: #2e7d32; }
.financeiro-kpi-out { background: var(--color-error-bg); color: var(--color-error); }
.financeiro-kpi-net { background: var(--color-accent-soft); color: var(--color-accent); }

.financeiro-resultado-block {
  margin-bottom: var(--space-lg);
}

.financeiro-subheading {
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0 var(--space-sm);
  color: var(--color-text-secondary);
}

.financeiro-chart-container {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.financeiro-chart-container :deep(.apexcharts-canvas),
.financeiro-chart-container :deep(svg) {
  max-width: 100%;
}

.financeiro-summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  margin-top: var(--space-md);
  table-layout: fixed;
}

.financeiro-summary-table th,
.financeiro-summary-table td {
  padding: var(--space-xs) var(--space-sm);
  border-bottom: 1px solid var(--color-border);
}

.financeiro-summary-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.financeiro-summary-table tfoot th {
  font-weight: 700;
  border-top: 2px solid var(--color-border);
}

.amount-in { color: #2e7d32; }
.amount-out { color: var(--color-error); }

@media (max-width: 640px) {
  .financeiro-kpis {
    grid-template-columns: 1fr;
  }

  .financeiro-subheading {
    font-size: 0.8rem;
  }

  .financeiro-summary-table {
    font-size: 0.8125rem;
  }

  .financeiro-summary-table th,
  .financeiro-summary-table td {
    padding: var(--space-xs);
  }
}
</style>
